class NumericStringBuilder {
    
    private _value: string = "0"
    private _displayThousandsDelimiter: boolean = true
    private _intOnly: boolean = false
    private _positiveOnly: boolean = false
    private _roundFiguresOnLengthExceeded: boolean = false
    private _max : number = Number.POSITIVE_INFINITY
    private _min : number = Number.NEGATIVE_INFINITY
    private _decimals : number = 5
    private _type : "es" | "us" = "us"

    locale(value: "es" | "us") : NumericStringBuilder {
        this._type = value
        return this
    }

    value(value: string) : NumericStringBuilder {
        this._value = value
        return this
    }

    displayThousandsDelimiter(value: boolean) : NumericStringBuilder {
        this._displayThousandsDelimiter = value
        return this
    }

    positiveOnly(value: boolean) : NumericStringBuilder {
        this._positiveOnly = value
        return this
    }

    intOnly(value: boolean) : NumericStringBuilder {
        this._intOnly = value
        return this
    }

    roundFiguresOnLengthExceeded(value: boolean) {
        this._roundFiguresOnLengthExceeded = value
        return this
    }

    max(value: number) : NumericStringBuilder { 
        this._max = value
        return this
    }

    min(value: number) : NumericStringBuilder { 
        this._min = value
        return this
    }

    decimals(value: number) : NumericStringBuilder {
        this._decimals = value
        return this
    }

    empty() : NumericString {
        return this.value("").build()
    }

    zero(): NumericString {
        return this.value("0").build()
    }

    build() : NumericString {
        const decimalsDelimiter = this._type === "us" ? "." : ","
        const thousandsDelimiter = this._type === "us" ? "," : "."
        return new NumericString(this._value, thousandsDelimiter, decimalsDelimiter, this._displayThousandsDelimiter, this._intOnly, this._positiveOnly, this._min, this._max, this._decimals, this._roundFiguresOnLengthExceeded)
    }
}
export class NumericString {
    
    private hasDecimalSymbol = false
    private hasNegativeSymbol = false
    
    constructor(private value: string, public thousandsDelimiter: string, public decimalDelimiter: string, private displayThousandsDelimiter: boolean, private intOnly: boolean, private positiveOnly: boolean, private min: number, private max : number, private decimals: number, private roundFiguresOnLengthExceeded: boolean) { 
        if(this.value === this.decimalDelimiter) this.value = `0${this.decimalDelimiter}`
        this.value = this.shouldBeRounded() ? this.round() : this.trim()
        this.hasDecimalSymbol = this.value.includes(this.decimalDelimiter)
        this.hasNegativeSymbol = value.startsWith("-")
    }

    static builder() : NumericStringBuilder {
        return new NumericStringBuilder()
    }

    isValid() : boolean {
        if(this.isEmpty() || this.isFloatForRequiredInteger() || this.isNegativeForRequiredPositive() || this.isNotInRange()) return false
        const regexString = `^[-0-9${this.thousandsDelimiter}]*\\${this.decimalDelimiter}?[0-9]*$`
        const regex = new RegExp(regexString, 'g')
        if (this.value.match(regex) && this.value.match(regex)!!.length > 0) {
            const tokens = this.value.split(this.thousandsDelimiter)
            let index = 0
            tokens.forEach(token => {
                if(index !== 0 && token.length !== 3)
                    return false
                index++
            })
            return true
        } else return false
    }

    isNegative() : boolean {
        return this.value.startsWith("-")
    }
    
    getNumericValue(decimalsDelimiter: string = ".") {
        return this.value.replaceAll(this.thousandsDelimiter, "").replace(this.decimalDelimiter, decimalsDelimiter)
    }

    getPrintingValue() {
        const decimalValue = this.getDecimalValue() ? this.getDecimalValue() : ""
        const decimalsString = this.hasDecimalSymbol ? `${this.decimalDelimiter}${decimalValue}` : ""
        const negativeString = this.hasNegativeSymbol ? "-" : ""
        const integerString = this.displayThousandsDelimiter ? this.separateThousands() : this.getIntegerValue()
        return negativeString + integerString + decimalsString
    }

    private isEmpty() : boolean {
        return this.value.trim().length === 0
    }

    private isFloatForRequiredInteger() : boolean {
        return this.intOnly && this.value.trim().includes(this.decimalDelimiter)
    }

    private isNegativeForRequiredPositive() : boolean {
        return this.positiveOnly && this.value.trim().startsWith("-")
    }
    
    private isNotInRange() {
        if(this.min <= 0 && this.value === "-") return false
        return (this.min <= Number(this.getNumericValue()) && Number(this.getNumericValue()) <= this.max) === false
    }

    private shouldBeRounded() {
        return this.roundFiguresOnLengthExceeded && this.getDecimalValue()?.length > this.decimals
    }

    private trim(): string {
        if(this.value.indexOf(this.decimalDelimiter) > 0)
            return this.value.substring(0, this.value.indexOf(this.decimalDelimiter) + this.decimals + 1)
        else 
            return this.value
    }
    
    private round(): string {
        const str = `0.${this.getDecimalValue()}`
        const numericValue = +str * Math.pow(10, this.decimals)
        return (+this.getIntegerValue() +  (Math.round(numericValue) / Math.pow(10, this.decimals))).toString().replaceAll(".", this.decimalDelimiter)
    }

    private getIntegerValue() {
        return this.value.split(this.decimalDelimiter)[0].replaceAll(this.thousandsDelimiter, "").replaceAll("-", "")
    }

    private getDecimalValue() {
        return this.value.split(this.decimalDelimiter)[1] ?? undefined
    }

    private separateThousands() {
        return this.getIntegerValue().toString().replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsDelimiter);
    }
}
